// @givmo/app-admin — the employer BULK gift-card confirm→send→success dialog (EP-WEB5). ★ MONEY-MOVING ★
//
// A company bulk-sends N company-funded gift cards of a uniform denomination to roster employees via
// `POST /admin/company/gift-cards/batches`. Confirming makes ONE money movement for the whole batch —
// one COMPANY_CARD charge OR one COMPANY_WALLET debit (all-or-nothing: no partial mint). Every
// money-safety invariant mirrors the single-send `<SendGiftCardDialog>`:
//
//   1. IDEMPOTENCY — a stable key is minted ONCE per attempt-series and REUSED for any retry of the
//      SAME batch (same recipient SET, denomination, funding — order-independent). PAGE-OWNED holder, so
//      the series survives close/reopen. The SAME key rides BOTH the `Idempotency-Key` header AND the
//      body `purchase_request_id`; a same-key retry replays to the ORIGINAL batch, never a second charge.
//   2. NO BLIND RETRY of a MAYBE-CHARGED error (408/5xx/transport) — surface an explicit
//      check-status/try-again and re-POST under the SAME key only on the user's say-so; ack-gated dismiss.
//   3. FIRST-CLASS terminal states, each a DEFINITE pre-charge no-charge with the key DROPPED:
//      VELOCITY CAP (429), INSUFFICIENT FUNDS (409, wallet batch over the BATCH TOTAL), and the
//      ROSTER-INVALID 422 (BATCH_RECIPIENTS_INVALID — the whole batch rejected before any money moves,
//      with the per-entry offending rows rendered inline). The count cap (>500) is a 400 and lands in
//      the fatal branch with the server's message. As in the single-send, an EARLIER maybe-charged
//      attempt swaps "nothing was charged" for a check-first warning and keeps the dismissal ack-gated.
//   4. SINGLE-FLIGHT — one in-flight ref + a disabled button; a double-click cannot fire a second POST.
//   5. CONFIRM-BEFORE-CHARGE — restates the recipient count + per-card denomination + face total +
//      funding + the honest itemization before any charge; the batch is FIXED for the dialog session.
//   6. THE SIGNED NOTICE — the company's own notice renders in the terms panel directly above the send
//      button (GC-FU12b, `gift-disclaimer.ts`): Notice A on the company wallet, Notice C on a company
//      card — never the individual purchaser's v2.
//
// THE LAYOUT IS THE APPROVED CHARGE DIALOG'S (CAM-R60, D-CAM-41 → D-CAM-44): the batch is the focal
// point, as Approve all is on the approve dialog — its face total "for N gift cards", then one card
// holding where the money comes from and the itemization — and the terms sit under their "Terms"
// heading (D-CAM-97) after any error, adjacent to the button, at the Give dialog's width. The card's
// height is the design system's viewport clamp, so with the roster-invalid rows a phone still holds
// Cancel and Send on screen.
//
// THE SERVER'S PRICE (GC-FU31; signed Terms V6 clause 5): the face total is an exact count ×
// whole-dollar denomination. The COMPANY_CARD path is ONE charge of the GROSS for the whole batch (the
// face total + a processor gross-up the company always covers, D-GC-2) — never N single charges — so
// the confirm states the card processing and the total from the server's quote for that one charge
// (`gift-card-quote.ts`, `count` = the recipients), never a figure worked out here, and the send
// carries them (`expected_amount_charged` / `expected_fee`; `409 CHARGE_QUOTE_CHANGED` before money
// moves if the charge would take others, then the price is re-read under a new key). Send is held
// while the quote loads or has failed — a failed read is retried in the dialog, never by closing it,
// which resets the composed batch — and the stated price is frozen with the key, so a maybe-charged
// batch's retry sends the bytes it sent. The COMPANY_WALLET path debits EXACTLY the face total.
//
// RECEIPT COPY forks by funding on success (plan §3 — LAW): company_card ⇒ one neutral consolidated-
// receipt line; company_wallet ⇒ receipt-LESS. NEVER any recipient-receipt copy.
//
// ENQUEUE-FAILURE (D-EP-16(d)): the create-201 carries `delivery_enqueue_failed`; when > 0 those cards'
// codes are lost — the success state raises a PROMINENT void-&-reissue warning (never silent).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  sdk,
  firstErrorCode,
  idempotencyKey,
  isGivmoError,
  supportReference,
  GivmoError,
  type CompanyGiftCardBatchOut,
} from '@givmo/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, Icon, testIdProp } from '@givmo/design-system';
import { AlertTriangle, CheckCircle2, Gauge, Users, Wallet as WalletIcon } from 'lucide-react';
import { isRetryableChargeError, maybeChargedLine } from '@givmo/payments';
import { CHARGE_QUOTE_CHANGED } from '../../components/charge-quote';
import {
  fetchGiftCardQuote,
  giftCardQuoteQueryKey,
  statedGiftCardCharge,
  toGiftCardQuote,
} from './gift-card-quote';
import {
  formatGiftCardCharged,
  formatGiftCardDollars,
  giftCardReceiptCopy,
  isInsufficientFundsError,
  isVelocityCapError,
  type Funding,
} from './gift-cards-decode';
import {
  buildCompanyGiftCardBatchBody,
  batchEnqueueFailedCount,
  batchRecipientReasonLabel,
  extractInvalidBatchRecipients,
  formatBatchFaceTotal,
  isBatchRecipientsInvalidError,
  type BatchRecipient,
  type BatchRecipientError,
} from './gift-cards-batch-decode';
import type { GiftCardBatchSignature } from './gift-card-batch-idempotency';
import type { GiftCardChargeSeries, GiftCardHeldRequest } from './gift-card-idempotency';
import { ChargeSummary } from '../../components/charge-summary';
import { GiftDisclosureBlock } from './gift-disclosure-block';
import { companyGiftCardNotice } from './gift-disclaimer';
import { useCompanyGiftCardNotice } from './use-company-gift-card-notice';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { useRevealOnShow } from '../../components/reveal-on-show';

const PREFIX = 'gift-card-batch';

export interface SendGiftCardBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The resolved recipient set (work emails + optional names). */
  recipients: BatchRecipient[];
  /** The uniform whole-dollar denomination for every card (validated in [$15, $1,000] by the caller). */
  denomination: number;
  /** The chosen funding source: a specific company card, or the company wallet. */
  funding: Funding;
  /** Human-readable funding label restated in the confirm copy. */
  fundingLabel: string;
  /**
   * The funding source as the summary's "From" row names it ("Visa Card •••• 4242", "Company
   * wallet ($1,250.00 available)" — `fundingSummaryLabel`), as the Give dialog takes it. Absent →
   * the row shows `fundingLabel`.
   */
  fundingSummary?: string;
  /** The optional batch name to send (`name`), or null. */
  batchName: string | null;
  /** The optional shared message to send with every gift (`recipient_message`), or null. */
  recipientMessage: string | null;
  /**
   * The PAGE-OWNED charge series (one per page session, held in a page `useRef`): the idempotency key
   * and the request it was minted for — on a card, the price its one charge was stated at — so a
   * maybe-charged batch survives this dialog's unmount and is resent exactly as it went out.
   */
  series: GiftCardChargeSeries<GiftCardBatchSignature>;
  /** Fired ONCE when the batch confirms — the page invalidates the batches list + wallet reads. */
  onSuccess: (batch: CompanyGiftCardBatchOut) => void;
  /** Fired when the dialog closes FROM the success state — the page toasts + resets the form. */
  onCloseAfterSuccess: () => void;
  /** Fired when the dialog closes after a NON-success send attempt — the page refreshes its reads. */
  onCloseAfterAttempt: () => void;
}

/** The send state machine. `retryable-error` REUSES the idempotency key. The first-class terminal
 *  states carry `maybeChargedEarlier` (a prior attempt hit a maybe-charged error → don't claim
 *  "nothing charged", keep the dismissal ack-gated). `roster-invalid` also carries the per-entry rows. */
type Phase =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'retryable-error'; message: string; reference: string | null }
  | { status: 'insufficient-funds'; message: string; maybeChargedEarlier: boolean }
  | { status: 'velocity-cap'; message: string; maybeChargedEarlier: boolean }
  | {
      status: 'roster-invalid';
      message: string;
      rows: BatchRecipientError[];
      maybeChargedEarlier: boolean;
    }
  | { status: 'quote-changed'; maybeChargedEarlier: boolean }
  | { status: 'fatal-error'; message: string; reference: string | null }
  | { status: 'success'; batch: CompanyGiftCardBatchOut };

const RETRYABLE_HINT =
  'We could not confirm whether the batch went through. Do not assume it failed — check your ' +
  'batches, or try again. A retry is de-duplicated and will not charge twice.';

// The shortfall itself; what follows it — who adds funds, and where — is the host's
// (`words.giftCardAddFundsRetry`, EP-MNT5(h)).
const INSUFFICIENT_FUNDS_LEAD =
  'Your company wallet does not have enough available balance for this batch total.';

const VELOCITY_CAP_REASON =
  'You’ve reached your company’s gift-card sending limit for now. Please try again later.';

const ROSTER_INVALID_REASON =
  'Some recipients aren’t on your company roster, so the whole batch was declined — no cards were ' +
  'created. Fix the recipients below and try again.';

const NOTHING_CHARGED_TAIL = 'Nothing was charged.';
const COULD_NOT_CONFIRM_EARLIER_TAIL =
  'We couldn’t confirm your earlier attempt — check your batches before retrying.';

const CLOSE_ACK_BODY =
  'The charge may have gone through even though we could not confirm it. After closing, check your ' +
  'recent batches before sending again — re-sending the exact same batch (same recipients, amount, ' +
  'and payment method) is safe and will not double-charge, but a different batch would be a new charge.';

// GC-FU31: what a card batch says when it cannot read the server's price (Send held, no figure), with
// the price check retried IN the dialog: closing resets the batch the admin composed, its recipients
// included (review M1). The "Try again" after the sentence is the control.
const QUOTE_FAILED =
  'We couldn’t check the card processing for this batch, so it can’t be sent yet.';
const QUOTE_RETRY = 'Try again';
// After an attempt that may have charged, the check-first tail takes the place of "nothing was
// charged".
const QUOTE_CHANGED =
  'The price of this batch changed — its card processing or its total — so nothing was charged. ' +
  'Check the new figures, then send again.';
const QUOTE_CHANGED_LEAD = 'The price of this batch changed — its card processing or its total.';

export function SendGiftCardBatchDialog({
  open,
  onOpenChange,
  recipients,
  denomination,
  funding,
  fundingLabel,
  fundingSummary,
  batchName,
  recipientMessage,
  series,
  onSuccess,
  onCloseAfterSuccess,
  onCloseAfterAttempt,
}: SendGiftCardBatchDialogProps) {
  // Who adds funds, and where, is this host's (EP-MNT5(h)) — words only; the charge path is untouched.
  const { words } = useEmployerModulePaths();
  const [phase, setPhase] = useState<Phase>({ status: 'idle' });
  const [closeAck, setCloseAck] = useState(false);

  const inFlight = useRef(false);
  const attempted = useRef(false);
  const sawRetryable = useRef(false);

  const queryClient = useQueryClient();

  // The signed notice for this funding source (GC-FU12b): Notice A on the wallet, Notice C on a card —
  // its LIVE row while the confirm is open, its floor (the same signed bytes) until then or on failure.
  const notice = companyGiftCardNotice(funding);
  const shownNotice = useCompanyGiftCardNotice(notice, open);

  // The server's price for a CARD batch's ONE charge (GC-FU31): `count` cards at this denomination on
  // this card. `staleTime: 0` — read fresh each time it is stated. None on the wallet.
  const quoteCount = recipients.length;
  const cardId = funding.kind === 'card' ? funding.cardId : null;
  const quoteKey = giftCardQuoteQueryKey(denomination, quoteCount, cardId ?? '');
  const quoteQuery = useQuery({
    queryKey: quoteKey,
    queryFn: () => fetchGiftCardQuote(denomination, quoteCount, cardId ?? ''),
    enabled: open && cardId !== null && quoteCount > 0,
    select: (body: unknown) => toGiftCardQuote(body, { amount: denomination, count: quoteCount }),
    staleTime: 0,
  });
  // A failed read being retried in place is "loading" again: no figure, Send spinning.
  const quoteStatus: 'none' | 'loading' | 'failed' | 'ready' =
    cardId === null
      ? 'none'
      : quoteQuery.isPending
        ? 'loading'
        : quoteQuery.isError || quoteQuery.data == null
          ? quoteQuery.isFetching
            ? 'loading'
            : 'failed'
          : 'ready';
  const liveQuote = quoteStatus === 'ready' ? (quoteQuery.data ?? null) : null;

  const signature = useMemo<GiftCardBatchSignature>(
    () => ({
      recipientWorkEmails: recipients.map((r) => r.workEmail),
      denomination,
      funding,
    }),
    [recipients, denomination, funding],
  );

  // Reset ONLY on a genuine CLOSED→OPEN transition. The idempotency holder is DELIBERATELY NOT cleared
  // here (nor on unmount — it is page-owned): persisting it keeps a maybe-charged batch's series alive.
  // A request still held at open is an attempt this series never confirmed — it is held from its send
  // until a success or a definite no-charge, and the dialog cannot close mid-flight — so a definite
  // refusal of the resend keeps the check-first framing and the ack gate (constraint 3, review L2).
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setPhase({ status: 'idle' });
      setCloseAck(false);
      attempted.current = false;
      inFlight.current = false;
      sawRetryable.current = series.heldFor(signature) !== null;
    }
    prevOpen.current = open;
  }, [open, series, signature]);

  const busy = phase.status === 'sending';
  const count = recipients.length;

  // The request held for this batch (in flight, in doubt, or re-entered after a maybe-charged close):
  // while one is held the dialog states the price it will RESEND, never a live one that moved since.
  const heldRequest = series.heldFor(signature);
  const shownQuote = heldRequest?.quote ?? liveQuote;

  const finalizeClose = useCallback(() => {
    if (phase.status === 'success') {
      onCloseAfterSuccess();
    } else if (attempted.current) {
      onCloseAfterAttempt();
    }
    onOpenChange(false);
  }, [phase.status, onCloseAfterSuccess, onCloseAfterAttempt, onOpenChange]);

  // Dismissal must route through the explicit acknowledgement when a maybe-charged (`retryable-error`)
  // state is showing, OR a terminal 4xx REACHED FROM a prior maybe-charged attempt (unconfirmed).
  const dismissNeedsAck =
    phase.status === 'retryable-error' ||
    ((phase.status === 'velocity-cap' ||
      phase.status === 'insufficient-funds' ||
      phase.status === 'roster-invalid' ||
      phase.status === 'quote-changed') &&
      phase.maybeChargedEarlier);
  // The acknowledgement replaces the footer, so on a phone it opens below the fold: it is brought into
  // view (P5).
  const closeAckRef = useRevealOnShow<HTMLDivElement>(dismissNeedsAck && closeAck);

  const requestClose = useCallback(() => {
    if (busy) return;
    if (dismissNeedsAck) {
      setCloseAck(true);
      return;
    }
    finalizeClose();
  }, [busy, dismissNeedsAck, finalizeClose]);

  const runSend = useCallback(async () => {
    if (inFlight.current) return; // single-flight

    // The request this send makes: the one held for this batch — "Try again", or the same batch
    // re-entered after a close — exactly as it was stated; else a new one, stated from the price the
    // dialog shows now. A card batch never goes out without that price (the control is held too).
    let request: GiftCardHeldRequest<GiftCardBatchSignature> | null = series.heldFor(signature);
    if (request === null) {
      if (funding.kind === 'card' && liveQuote === null) return;
      request = { signature, quote: funding.kind === 'card' ? liveQuote : null };
    }
    inFlight.current = true;
    attempted.current = true;

    const key = series.keyFor(request);
    const body = buildCompanyGiftCardBatchBody({
      denomination,
      funding,
      recipients,
      batchName,
      recipientMessage,
      purchaseRequestId: key,
      stated: request.quote === null ? null : statedGiftCardCharge(request.quote),
    });

    try {
      setPhase({ status: 'sending' });
      const { data } = await sdk.companiesSendCompanyGiftCardBatch({
        body,
        ...idempotencyKey(key),
        throwOnError: true,
      });

      series.clear();
      inFlight.current = false;
      onSuccess(data);
      setPhase({ status: 'success', batch: data });
    } catch (caught: unknown) {
      inFlight.current = false;
      const err = isGivmoError(caught)
        ? caught
        : new GivmoError({ message: 'Something went wrong. Please try again.', kind: 'unknown' });

      // VELOCITY CAP (429) — definite pre-charge no-charge; checked BEFORE the shared retryable
      // classifier (which treats 429 as retryable). Drop the key.
      if (isVelocityCapError(err)) {
        series.clear();
        const maybeChargedEarlier = sawRetryable.current;
        setPhase({
          status: 'velocity-cap',
          message: `${VELOCITY_CAP_REASON} ${
            maybeChargedEarlier ? COULD_NOT_CONFIRM_EARLIER_TAIL : NOTHING_CHARGED_TAIL
          }`,
          maybeChargedEarlier,
        });
        return;
      }

      // INSUFFICIENT FUNDS (409) — definite no-charge. Drop the key.
      if (isInsufficientFundsError(err)) {
        series.clear();
        const maybeChargedEarlier = sawRetryable.current;
        setPhase({
          status: 'insufficient-funds',
          message: `${INSUFFICIENT_FUNDS_LEAD} ${words.giftCardAddFundsRetry} ${
            maybeChargedEarlier ? COULD_NOT_CONFIRM_EARLIER_TAIL : NOTHING_CHARGED_TAIL
          }`,
          maybeChargedEarlier,
        });
        return;
      }

      // ROSTER-INVALID (422 BATCH_RECIPIENTS_INVALID) — the WHOLE batch rejected BEFORE any money
      // moves, with the per-entry offending rows. Definite no-charge, NON-retryable (fix recipients).
      // Drop the key. Checked before the generic fatal branch to render the per-entry list.
      if (isBatchRecipientsInvalidError(err)) {
        series.clear();
        const maybeChargedEarlier = sawRetryable.current;
        setPhase({
          status: 'roster-invalid',
          message: `${ROSTER_INVALID_REASON} ${
            maybeChargedEarlier ? COULD_NOT_CONFIRM_EARLIER_TAIL : NOTHING_CHARGED_TAIL
          }`,
          rows: extractInvalidBatchRecipients(err),
          maybeChargedEarlier,
        });
        return;
      }

      // PRICE CHANGED (409 CHARGE_QUOTE_CHANGED, GC-FU31) — the stated card processing or total is no
      // longer what the batch's one charge would take, refused before any money moved: a definite
      // no-charge for this attempt. Drop the key and RESET the refused price (an invalidated query
      // would state it while it refetches, with Send live). Same earlier-attempt caveat as above.
      if (firstErrorCode(err) === CHARGE_QUOTE_CHANGED) {
        series.clear();
        void queryClient.resetQueries({ queryKey: quoteKey });
        setPhase({ status: 'quote-changed', maybeChargedEarlier: sawRetryable.current });
        return;
      }

      const reference = supportReference(err);
      // RETRYABLE (maybe-charged — 408/5xx/transport): KEEP the key; explicit try-again only. Its line
      // is the server's own sentence where the server worded one, never a processor's (EP-WEB34).
      if (isRetryableChargeError(err)) {
        sawRetryable.current = true;
        setPhase({
          status: 'retryable-error',
          message: maybeChargedLine(err, RETRYABLE_HINT),
          reference,
        });
        return;
      }

      // FATAL (definite no-charge — 400 count-cap / 401 / 403 / other): drop the key; the backend
      // replays a cached completed 4xx under the same key, so reuse would replay the failure.
      series.clear();
      setPhase({ status: 'fatal-error', message: err.message, reference });
    }
  }, [
    signature,
    denomination,
    funding,
    recipients,
    batchName,
    recipientMessage,
    series,
    liveQuote,
    queryClient,
    quoteKey,
    onSuccess,
    words,
  ]);

  const isSuccess = phase.status === 'success';
  const title = isSuccess ? 'Batch sent' : 'Confirm your gift-card batch';
  const denominationLabel = formatGiftCardDollars(denomination);
  const faceTotalLabel = formatBatchFaceTotal(denomination, count);

  // Fork the confirm verb + the itemization by funding source: on a card, the server's card processing
  // and total for the ONE charge (the held request's while one is held), never worked out here; while
  // there is no price the figures say so and Send is held. The wallet debits exactly the face total.
  const processingFigure =
    shownQuote === null ? null : (formatGiftCardCharged(shownQuote.fee) ?? shownQuote.fee);
  const totalFigure =
    shownQuote === null
      ? null
      : (formatGiftCardCharged(shownQuote.amountCharged) ?? shownQuote.amountCharged);
  const noFigure = quoteStatus === 'failed' ? 'Unavailable' : 'Checking…';
  // No price and none held: the line says why and offers the price check again, in place.
  const quoteFailed = funding.kind === 'card' && quoteStatus === 'failed' && heldRequest === null;
  const confirmVerb =
    funding.kind === 'wallet'
      ? `This debits your company wallet ${faceTotalLabel} immediately.`
      : 'This charges your company card immediately.';
  const totalLabel =
    funding.kind === 'wallet' ? 'Debited from wallet today' : 'Total charged today';
  const totalValue = funding.kind === 'wallet' ? faceTotalLabel : (totalFigure ?? noFigure);
  // A card batch is armed once its price is in; a held request is resent as it was and needs none.
  const priceHeldBack = funding.kind === 'card' && heldRequest === null && liveQuote === null;

  const receiptLine = giftCardReceiptCopy(funding);

  const enqueueFailed = isSuccess ? batchEnqueueFailedCount(phase.batch) : 0;
  // The batch's exact GROSS charged, surfaced from the create-201 response (EP-FIX3 / GC1-CMP1) —
  // present only for a COMPANY_CARD batch; null for a wallet batch (face only), never recomputed.
  const successCharged = isSuccess ? formatGiftCardCharged(phase.batch.amount_charged) : null;

  // One type-narrowed view of the current simple error state (roster-invalid renders its own block).
  const errorView =
    phase.status === 'retryable-error'
      ? {
          testId: `${PREFIX}-retryable-error`,
          icon: AlertTriangle,
          message: phase.message,
          reference: phase.reference,
        }
      : phase.status === 'insufficient-funds'
        ? {
            testId: `${PREFIX}-insufficient-funds`,
            icon: WalletIcon,
            message: phase.message,
            reference: null,
          }
        : phase.status === 'velocity-cap'
          ? {
              testId: `${PREFIX}-velocity-cap`,
              icon: Gauge,
              message: phase.message,
              reference: null,
            }
          : phase.status === 'quote-changed'
            ? {
                testId: `${PREFIX}-quote-changed`,
                icon: AlertTriangle,
                message: phase.maybeChargedEarlier
                  ? `${QUOTE_CHANGED_LEAD} ${COULD_NOT_CONFIRM_EARLIER_TAIL}`
                  : QUOTE_CHANGED,
                reference: null,
              }
            : phase.status === 'fatal-error'
              ? {
                  testId: `${PREFIX}-fatal-error`,
                  icon: AlertTriangle,
                  message: phase.message,
                  reference: phase.reference,
                }
              : null;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          requestClose();
          return;
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label={title}
        className="flex flex-col gap-16"
        width={480}
        testId={`${PREFIX}-confirm-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>

        {phase.status === 'success' ? (
          // ── Success: forked receipt copy + the enqueue-failure warning (D-EP-16(d)). ──
          <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-success`)}>
            <div className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-12">
              <Icon icon={CheckCircle2} size={18} aria-hidden className="mt-1 text-success-600" />
              <div className="flex flex-col gap-4">
                <p className="font-sans text-s14 font-w500 leading-h15 tracking-givmo text-text-strong">
                  You sent {count} gift card{count === 1 ? '' : 's'} of {denominationLabel} each (
                  {faceTotalLabel} face value).
                </p>
                {successCharged !== null && (
                  <p
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary tabular-nums"
                    {...testIdProp(`${PREFIX}-success-charged`)}
                  >
                    Charged {successCharged}, including card processing.
                  </p>
                )}
                {receiptLine !== null && (
                  <p
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    {...testIdProp(`${PREFIX}-success-receipt`)}
                  >
                    {receiptLine}
                  </p>
                )}
              </div>
            </div>

            {enqueueFailed > 0 && (
              <div
                className="flex items-start gap-8 rounded-lg border border-danger-100 bg-danger-50 p-12"
                {...testIdProp(`${PREFIX}-enqueue-failed`)}
              >
                <Icon icon={AlertTriangle} size={16} aria-hidden className="mt-1 text-danger-700" />
                <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                  {enqueueFailed} card{enqueueFailed === 1 ? '' : 's'} could not be queued for
                  delivery — contact support to void &amp; reissue{' '}
                  {enqueueFailed === 1 ? 'it' : 'them'}.
                </p>
              </div>
            )}

            <div className="mt-4 flex items-center justify-end gap-8">
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                onClick={finalizeClose}
                testId={`${PREFIX}-done`}
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* ── The batch is the focal point, as Approve all is on the approve dialog (D-CAM-44): its
                face total "for N gift cards" at the per-card denomination, then one card holding the
                itemization — the source, (card only) the server's card processing for the one charge,
                and the funding-forked total. It stays ABOVE the terms. ── */}
            <ChargeSummary
              amount={faceTotalLabel}
              caption={`for ${count} gift card${count === 1 ? '' : 's'}`}
              context={`${denominationLabel} each`}
              rows={[
                {
                  label: 'From',
                  value: fundingSummary ?? fundingLabel,
                  testId: `${PREFIX}-summary-from`,
                },
                ...(funding.kind === 'card'
                  ? [
                      {
                        label: 'Card processing',
                        value: processingFigure ?? noFigure,
                        testId: `${PREFIX}-summary-processing`,
                      },
                    ]
                  : []),
                { label: totalLabel, value: totalValue, testId: `${PREFIX}-total` },
              ]}
              testId={`${PREFIX}-itemization`}
            />

            <Dialog.Description
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${PREFIX}-charge-line`)}
            >
              {quoteFailed ? (
                <>
                  {QUOTE_FAILED}{' '}
                  <button
                    type="button"
                    onClick={() => void quoteQuery.refetch()}
                    className="font-w600 text-brand-600 outline-none hover:underline focus-visible:underline"
                    {...testIdProp(`${PREFIX}-quote-retry`)}
                  >
                    {QUOTE_RETRY}
                  </button>
                </>
              ) : (
                confirmVerb
              )}
            </Dialog.Description>

            {/* ── Roster-invalid: the per-entry offending rows (all-or-nothing 422). ── */}
            {phase.status === 'roster-invalid' && (
              <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-roster-invalid`)}>
                <div className="flex items-start gap-8">
                  <Icon icon={Users} size={16} aria-hidden className="mt-1 text-danger-700" />
                  <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                    {phase.message}
                  </p>
                </div>
                {phase.rows.length > 0 && (
                  <ul
                    className="flex max-h-[160px] flex-col gap-4 overflow-y-auto rounded-lg border border-danger-100 bg-danger-50 p-12"
                    {...testIdProp(`${PREFIX}-roster-invalid-rows`)}
                  >
                    {phase.rows.map((row, i) => (
                      <li
                        key={`${row.workEmail ?? 'row'}-${row.index ?? i}`}
                        className="flex items-center justify-between gap-8 font-sans text-s12 tracking-givmo text-danger-700"
                        {...testIdProp(`${PREFIX}-roster-invalid-row-${row.index ?? i}`)}
                      >
                        <span className="truncate">{row.workEmail || '(blank)'}</span>
                        <span className="shrink-0 font-w500">
                          {batchRecipientReasonLabel(row.reason)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {errorView && (
              <div className="flex flex-col items-start gap-2" {...testIdProp(errorView.testId)}>
                <div className="flex items-start gap-8">
                  <Icon
                    icon={errorView.icon}
                    size={16}
                    aria-hidden
                    className="mt-1 text-danger-700"
                  />
                  <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                    {errorView.message}
                  </p>
                </div>
                {errorView.reference !== null && (
                  <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
                    {errorView.reference}
                  </p>
                )}
              </div>
            )}

            {/* ── The company's signed notice for this funding source, directly above the send
                button: after the errors, as the Give dialog places its terms (D-CAM-41, "adjacent to
                the button"). ── */}
            <GiftDisclosureBlock notice={notice} shown={shownNotice} />

            {dismissNeedsAck && closeAck ? (
              <div
                ref={closeAckRef}
                className="flex scroll-mb-24 flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken p-12"
                {...testIdProp(`${PREFIX}-close-ack`)}
              >
                <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary">
                  {CLOSE_ACK_BODY}
                </p>
                <div className="flex items-center justify-end gap-8">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="md"
                    onClick={() => setCloseAck(false)}
                    testId={`${PREFIX}-close-ack-back`}
                  >
                    Go back
                  </Button>
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="md"
                    onClick={finalizeClose}
                    testId={`${PREFIX}-close-ack-confirm`}
                  >
                    I understand — close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex items-center justify-end gap-8">
                <Dialog.Close
                  disabled={busy}
                  render={<Button appearance="saas" tone="secondary" size="md" />}
                >
                  Cancel
                </Dialog.Close>
                <Button
                  appearance="saas"
                  tone="primary"
                  size="md"
                  // Terminal states (insufficient funds / velocity cap / roster-invalid) offer no
                  // retry — the user must change funding, wait, or fix recipients.
                  disabled={
                    busy ||
                    phase.status === 'insufficient-funds' ||
                    phase.status === 'velocity-cap' ||
                    phase.status === 'roster-invalid' ||
                    priceHeldBack
                  }
                  loading={busy || (priceHeldBack && quoteStatus === 'loading')}
                  onClick={() => void runSend()}
                  testId={`${PREFIX}-confirm-submit`}
                >
                  {phase.status === 'retryable-error' ? 'Try again' : `Send ${faceTotalLabel}`}
                </Button>
              </div>
            )}
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
